import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox, Alert, Snackbar,
  Autocomplete, Chip, Stack,
} from '@mui/material';
import { Add as AddIcon, Upload as UploadIcon, Hub as HubIcon } from '@mui/icons-material';
import { useUsers, useNodes } from '../hooks/useNodes';
import { UserTable } from '../components/UserTable';
import { BatchImportDialog } from '../components/BatchImportDialog';
import { createUser, addUserNode } from '../api/users';
import type { CreateUserRequest, NodeResponse } from '../api/types';

export function UsersPage() {
  const qc = useQueryClient();
  const { data: users, isLoading } = useUsers();
  const { data: nodes } = useNodes();

  // 批量选择
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // 单个添加用户对话框
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<CreateUserRequest>({
    synoUsername: '', displayName: '', email: '', department: '', isAdmin: false,
  });

  // 批量导入对话框
  const [importOpen, setImportOpen] = useState(false);

  // 批量分配节点对话框
  const [batchAssignOpen, setBatchAssignOpen] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<NodeResponse[]>([]);
  const [assigning, setAssigning] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: '' });

  // ---- 单个添加 ----
  const handleOpen = () => {
    setForm({ synoUsername: '', displayName: '', email: '', department: '', isAdmin: false });
    setError('');
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setError(''); };
  const handleSave = async () => {
    if (!form.synoUsername.trim()) { setError('用户名不能为空'); return; }
    setSaving(true); setError('');
    try {
      await createUser({ synoUsername: form.synoUsername.trim(), displayName: form.displayName?.trim() || undefined, email: form.email?.trim() || undefined, department: form.department?.trim() || undefined, isAdmin: form.isAdmin });
      setSnack({ open: true, msg: `用户 ${form.synoUsername} 创建成功` });
      setOpen(false); qc.invalidateQueries({ queryKey: ['users'] });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '创建失败');
    } finally { setSaving(false); }
  };

  // ---- 批量分配节点 ----
  const handleBatchAssign = async () => {
    if (selectedIds.length === 0 || selectedNodes.length === 0) return;
    setAssigning(true);
    let success = 0;
    for (const userId of selectedIds) {
      for (const node of selectedNodes) {
        try { await addUserNode(userId, node.id); success++; } catch { /* skip */ }
      }
    }
    setAssigning(false);
    setBatchAssignOpen(false);
    setSnack({ open: true, msg: `已为 ${selectedIds.length} 个用户分配 ${selectedNodes.length} 个节点` });
    setSelectedIds([]);
    setSelectedNodes([]);
  };

  const activeNodes = (nodes || []).filter(n => n.isActive);

  return (
    <Box>
      {/* 标题栏 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600}>用户管理</Typography>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
            批量导入
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
            添加用户
          </Button>
        </Stack>
      </Box>

      {/* 批量操作栏 */}
      {selectedIds.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 2, py: 1, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
          <Chip label={`已选 ${selectedIds.length} 个用户`} color="primary" size="small" onDelete={() => setSelectedIds([])} />
          <Button
            size="small"
            variant="contained"
            startIcon={<HubIcon />}
            onClick={() => setBatchAssignOpen(true)}
          >
            批量分配节点
          </Button>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        用户通过群晖 SSO 首次登录时自动创建。可手动添加或批量导入。点击行展开查看/管理节点分配。
      </Typography>

      <UserTable
        users={users || []}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {/* 单个添加用户对话框 */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>添加用户（预注册）</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            输入群晖用户名即可预注册用户，该用户首次通过 SSO 登录时信息会自动更新。
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField autoFocus margin="dense" label="群晖用户名 *" placeholder="与群晖 DSM 账号一致" fullWidth value={form.synoUsername} onChange={e => setForm(f => ({ ...f, synoUsername: e.target.value }))} sx={{ mb: 2 }} />
          <TextField margin="dense" label="显示名称" placeholder="可选，默认使用用户名" fullWidth value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} sx={{ mb: 2 }} />
          <TextField margin="dense" label="邮箱" placeholder="可选" fullWidth value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} sx={{ mb: 2 }} />
          <TextField margin="dense" label="部门" placeholder="可选" fullWidth value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} sx={{ mb: 1 }} />
          <FormControlLabel control={<Checkbox checked={!!form.isAdmin} onChange={e => setForm(f => ({ ...f, isAdmin: e.target.checked }))} />} label="设为管理员" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>取消</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? '创建中...' : '创建'}</Button>
        </DialogActions>
      </Dialog>

      {/* 批量导入对话框 */}
      <BatchImportDialog open={importOpen} onClose={() => setImportOpen(false)} onSuccess={() => qc.invalidateQueries({ queryKey: ['users'] })} />

      {/* 批量分配节点对话框 */}
      <Dialog open={batchAssignOpen} onClose={() => setBatchAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>批量分配节点</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            为已选的 {selectedIds.length} 个用户分配节点。已分配的节点会自动跳过（幂等）。
          </Typography>
          <Autocomplete
            multiple
            options={activeNodes}
            getOptionLabel={(node) => `${node.name} (${node.address}:${node.port})`}
            value={selectedNodes}
            onChange={(_, val) => setSelectedNodes(val)}
            renderInput={(params) => (
              <TextField {...params} label="选择节点" placeholder="可多选" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((node, index) => (
                <Chip
                  label={node.name}
                  size="small"
                  {...getTagProps({ index })}
                  key={node.id}
                />
              ))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchAssignOpen(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={handleBatchAssign}
            disabled={assigning || selectedNodes.length === 0}
          >
            {assigning ? '分配中...' : `分配 ${selectedNodes.length} 个节点`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} message={snack.msg} />
    </Box>
  );
}
